import React from 'react';
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import GradeOutlinedIcon from '@mui/icons-material/GradeOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import {
  CODING_PROGRESS_MATCH,
  STUDENT_ASSIGNMENTS_LABS_MATCH,
  STUDENT_LAB_RESULTS_MATCH,
  STUDENT_PROGRESS_MATCH,
} from './isActiveRoute';
import { isCbcFaculty } from '../../utils/facultyRole';

/** @typedef {{ id: string, label: string, path?: string, matchPath?: string, icon: React.ReactNode, action?: 'logout' }} ResolvedNavItem */
/** @typedef {{ id: string, title?: string, initiallyExpanded?: boolean, collapsible?: boolean, items: ResolvedNavItem[] }} ResolvedNavSection */

const icon = {
  spaceDashboard: <SpaceDashboardOutlinedIcon fontSize="small" />,
  analytics: <AnalyticsOutlinedIcon fontSize="small" />,
  policy: <PolicyOutlinedIcon fontSize="small" />,
  dashboard: <DashboardOutlinedIcon fontSize="small" />,
  school: <SchoolOutlinedIcon fontSize="small" />,
  assignment: <AssignmentOutlinedIcon fontSize="small" />,
  tune: <TuneOutlinedIcon fontSize="small" />,
  people: <PeopleOutlineOutlinedIcon fontSize="small" />,
  lock: <LockOutlinedIcon fontSize="small" />,
  listAlt: <ListAltOutlinedIcon fontSize="small" />,
  quiz: <QuizOutlinedIcon fontSize="small" />,
  grade: <GradeOutlinedIcon fontSize="small" />,
  profile: <PersonOutlineIcon fontSize="small" />,
  menuBook: <MenuBookOutlinedIcon fontSize="small" />,
  science: <ScienceOutlinedIcon fontSize="small" />,
  accountTree: <AccountTreeOutlinedIcon fontSize="small" />,
  folder: <FolderOutlinedIcon fontSize="small" />,
  assessment: <AssessmentOutlinedIcon fontSize="small" />,
};

/** Top or bottom row(s) without a group header (aligned with dashboard nav density). */
function flatNavSection(id, items) {
  return /** @type {ResolvedNavSection} */ ({
    id,
    collapsible: false,
    items,
  });
}

const FACULTY_PROFILE_PATH = '/faculty/profile';

/** Shared teaching surfaces: same paths for `faculty` and `cbcfaculty` (see {@link RequireFacultyTeaching}). */
function facultyCourseManagementNavItems() {
  return [
    { id: 'courses', label: 'My courses', path: '/faculty/courses', icon: icon.school },
    { id: 'questions', label: 'Question bank', path: '/faculty/questions', icon: icon.quiz },
    { id: 'labs', label: 'Lab internals', path: '/faculty/lab-internals', icon: icon.science },
  ];
}

function facultyEvaluationNavItems() {
  return [
    { id: 'projects', label: 'Projects', path: '/faculty/projects', icon: icon.folder },
    { id: 'grading', label: 'Grading', path: '/faculty/grading', icon: icon.grade },
    {
      id: 'lab-results',
      label: 'Lab results',
      path: '/faculty/lab-results',
      icon: icon.assessment,
    },
  ];
}

/** CBC-only: coding tracker + progress + student activity (no duplication of teaching item defs). */
function cbcCodingWorkspaceNavItems() {
  return [
    {
      id: 'cw-dashboard',
      label: 'Coding dashboard',
      path: '/faculty/coding/overview',
      icon: icon.spaceDashboard,
    },
    {
      id: 'cw-progress',
      label: 'Progress analytics',
      path: '/faculty/coding-dashboard',
      matchPath: CODING_PROGRESS_MATCH,
      icon: icon.analytics,
    },
    {
      id: 'cw-student-activity',
      label: 'Student coding activity',
      path: '/faculty/coding/activity',
      icon: icon.people,
    },
  ];
}

function facultyProfileNavSection() {
  return flatNavSection('profile', [
    { id: 'profile-link', label: 'Profile', path: FACULTY_PROFILE_PATH, icon: icon.profile },
  ]);
}

/**
 * Admin nav: system control & monitoring only (no faculty teaching workflows).
 * Placeholder routes back `UnderConstruction` until dedicated admin views ship.
 */
export function getAdminNavSections() {
  const profilePath = '/admin/profile';

  return [
    flatNavSection('dashboard', [
      { id: 'admin-dashboard', label: 'Dashboard', path: '/admin/overview', icon: icon.dashboard },
    ]),
    {
      id: 'organization',
      title: 'Organization',
      initiallyExpanded: true,
      items: [
        {
          id: 'org-batches',
          label: 'Departments & batches',
          path: '/admin/coding-tracker',
          icon: icon.accountTree,
        },
        {
          id: 'org-users',
          label: 'Users (students & faculty)',
          path: '/admin/users',
          icon: icon.people,
        },
      ],
    },
    {
      id: 'academic-oversight',
      title: 'Academic oversight',
      initiallyExpanded: true,
      items: [
        {
          id: 'ac-courses',
          label: 'Courses overview',
          path: '/admin/courses-overview',
          icon: icon.menuBook,
        },
        {
          id: 'ac-labs',
          label: 'Lab activity',
          path: '/admin/lab-activity',
          icon: icon.science,
        },
      ],
    },
    {
      id: 'coding-insights',
      title: 'Coding insights',
      initiallyExpanded: true,
      items: [
        {
          id: 'ci-platform',
          label: 'Platform analytics',
          path: '/admin/platform-analytics',
          icon: icon.analytics,
        },
        {
          id: 'ci-plagiarism',
          label: 'Plagiarism insights',
          path: '/admin/plagiarism-analytics',
          icon: icon.policy,
        },
      ],
    },
    {
      id: 'system',
      title: 'System',
      initiallyExpanded: false,
      items: [
        { id: 'sys-settings', label: 'Settings', path: '/admin/coding-platforms', icon: icon.tune },
        { id: 'sys-access', label: 'Access control', path: '/admin/access-control', icon: icon.lock },
      ],
    },
    flatNavSection('profile', [
      { id: 'admin-profile', label: 'Profile', path: profilePath, icon: icon.profile },
    ]),
  ];
}

/**
 * Faculty sidebar: teaching vs CBC via {@link isCbcFaculty} (role `cbcfaculty` or future `facultyType`).
 * Course / lab / grading items are shared builders; CBC prepends a coding-first workspace.
 *
 * @param {{ user?: { role?: string, subtype?: string, facultyType?: string } }} ctx
 */
export function getFacultyNavSections(ctx) {
  const user = ctx.user;

  if (isCbcFaculty(user)) {
    return [
      flatNavSection('dashboard', [
        {
          id: 'cbc-dashboard',
          label: 'Dashboard',
          path: '/faculty/dashboard',
          icon: icon.dashboard,
        },
      ]),
      {
        id: 'coding-workspace',
        title: 'Coding workspace',
        initiallyExpanded: true,
        items: cbcCodingWorkspaceNavItems(),
      },
      {
        id: 'course-management',
        title: 'Course management',
        initiallyExpanded: false,
        items: facultyCourseManagementNavItems(),
      },
      {
        id: 'evaluation',
        title: 'Evaluation',
        initiallyExpanded: false,
        items: facultyEvaluationNavItems(),
      },
      facultyProfileNavSection(),
    ];
  }

  return [
    flatNavSection('dashboard', [
      { id: 'dash', label: 'Dashboard', path: '/faculty/dashboard', icon: icon.dashboard },
    ]),
    {
      id: 'course-management',
      title: 'Course management',
      initiallyExpanded: true,
      items: facultyCourseManagementNavItems(),
    },
    {
      id: 'evaluation',
      title: 'Evaluation',
      initiallyExpanded: true,
      items: facultyEvaluationNavItems(),
    },
    facultyProfileNavSection(),
  ];
}

/**
 * Student nav: action-first, minimal groups. Excludes project dashboard, question bank, grading.
 * `/student/coding/*` are placeholders until student coding UI is wired.
 */
export function getStudentNavSections() {
  const profilePath = '/student/profile';

  return [
    flatNavSection('dashboard', [
      { id: 'home', label: 'Dashboard', path: '/student/home', icon: icon.dashboard },
      { id: 'projects', label: 'My Projects', path: '/student/projects', icon: icon.folder },
    ]),
    {
      id: 'my-learning',
      title: 'My learning',
      initiallyExpanded: true,
      items: [
        { id: 'courses', label: 'My courses', path: '/student/courses', icon: icon.menuBook },
        {
          id: 'assignments-labs',
          label: 'Assignments & labs',
          path: '/student/lab-internals',
          matchPath: STUDENT_ASSIGNMENTS_LABS_MATCH,
          icon: icon.assignment,
        },
        {
          id: 'lab-results',
          label: 'Lab results',
          path: '/student/lab-results',
          matchPath: STUDENT_LAB_RESULTS_MATCH,
          icon: icon.assessment,
        },
      ],
    },
    {
      id: 'coding-practice',
      title: 'Coding practice',
      initiallyExpanded: true,
      items: [
        {
          id: 'coding-home',
          label: 'Coding dashboard',
          path: '/student/coding',
          icon: icon.spaceDashboard,
        },
        {
          id: 'coding-progress',
          label: 'Progress',
          path: '/student/progress',
          matchPath: STUDENT_PROGRESS_MATCH,
          icon: icon.analytics,
        },
      ],
    },
    flatNavSection('profile', [
      { id: 'profile-link', label: 'Profile', path: profilePath, icon: icon.profile },
    ]),
  ];
}
